import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { Button, ErrorMessage, IconButton, Loading, PhoneNumberInput, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast, clsx } from "@/utils";
import { EditExternalSupplierDetailsFormData, editExternalSupplierDetailsFormSchema } from "../../form";
import { useExternalSupplierProfileProfileContext } from "../../context";
import { useUpdateExternalSupplierMutation } from "@/redux/apis/externalSupplier/externalSupplierApi";
import { ExternalSupplierDetail } from "@/components/organisms/ExternalSuppliers/common/ExternalSupplierDetail";
import { TypeField } from "../../fields/TypeField";
import { StateField } from "../../fields/StateField";
import { getDirtyValues } from "@/helpers/formHelpers";

export const ExternalSupplierDetails = () => {
  const { externalSupplier, activeSection, setActiveSection, setActiveModal } = useExternalSupplierProfileProfileContext();
  const [updateExternalSupplier, { isLoading }] = useUpdateExternalSupplierMutation();

  const methods = useForm<EditExternalSupplierDetailsFormData>({
    resolver: zodResolver(editExternalSupplierDetailsFormSchema),
    defaultValues: {
      name: externalSupplier.name,
      type: externalSupplier.type,
      email: externalSupplier.email,
      keyContactName: externalSupplier.keyContactName,
      contactNumber: externalSupplier.phone,
    },
  });
  const { handleSubmit, register, control, formState: { errors: formErrors, isDirty, dirtyFields } } = methods;

  const handleEdit = () => {
    if (activeSection !== undefined) setActiveModal("save-changes");
    else setActiveSection("details");
  };

  const onSubmit = handleSubmit((data) => {
    if (isEditMode && isDirty) {
      const dirtyValues = getDirtyValues(dirtyFields, data) as EditExternalSupplierDetailsFormData;
      updateExternalSupplier({
        id: externalSupplier.id,
        name: data.name,
        type: data.type,
        // address_state: data.state,
        email: dirtyValues.email ? dirtyValues.email : "",
        key_contact_name: data.keyContactName,
        phone: data.contactNumber,
      })
        .unwrap()
        .then(() => {
          addToast("success", "Successfully updated external supplier details");
          setActiveSection(undefined);
        })
        .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
    }
  });

  const isEditMode = activeSection === "details";

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="relative mt-4 flex flex-col rounded-lg border border-neutral-gray bg-neutral-surface-gray p-5">
        {isLoading && <Loading />}
        <div className="flex flex-row items-center">
          <Typography variant="title" className="flex-1">
            External Supplier Details
          </Typography>
          {isEditMode ? (
            <div className="flex flex-row items-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => setActiveSection(undefined)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" type="submit" disabled={!isDirty}>
                Save
              </Button>
            </div>
          ) : (
            <IconButton variant="tertiary" iconName="Edit2" size="sm" onClick={handleEdit} />
          )}
        </div>
        <div
          className={clsx(
            "mt-6 grid gap-6",
            isEditMode ? "grid-cols-1 md:grid-cols-2 2xl:grid-cols-3" : "grid-cols-1 md:grid-cols-3 2xl:grid-cols-5"
          )}
        >
          <ExternalSupplierDetail
            editMode={isEditMode}
            label="Name"
            detail={
              isEditMode ? (
                <div className="flex flex-col gap-1">
                  <TextInput placeholder="Enter Name" maxLength={100} hasError={!!formErrors.name} {...register("name")} />
                  <ErrorMessage errors={formErrors} name="name" />
                </div>
              ) : (
                externalSupplier.name
              )
            }
          />
          <TypeField editMode={isEditMode} type={externalSupplier.type} />
          <StateField editMode={isEditMode} state={externalSupplier.state[0]} />
          <ExternalSupplierDetail
            label="Email"
            detail={
              isEditMode ? (
                <div className="flex flex-col gap-1">
                  <TextInput hasError={!!formErrors.email} placeholder="Enter Email" {...register("email")} />
                  <ErrorMessage errors={formErrors} name="email" />
                </div>
              ) : externalSupplier.email
            }
          />
          <ExternalSupplierDetail
            editMode={isEditMode}
            label="Key Contact Name"
            detail={
              isEditMode ? (
                <div className="flex flex-col gap-1">
                  <TextInput placeholder="Enter Key Contact Name" maxLength={50} hasError={!!formErrors.name} {...register("keyContactName")} />
                  <ErrorMessage errors={formErrors} name="keyContactName" />
                </div>
              ) : externalSupplier.keyContactName
            }
          />
          <ExternalSupplierDetail
            editMode={isEditMode}
            label="Contact Number"
            detail={
              isEditMode ? (
                <div className="flex flex-col gap-1">
                  <PhoneNumberInput control={control} name="contactNumber" hasError={!!formErrors.contactNumber} />
                  <ErrorMessage errors={formErrors} name="contactNumber" />
                </div>
              ) : externalSupplier.phone
            }
          />
        </div>
      </form>
    </FormProvider>
  );
};
